import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum } from '@kheops/graphql';
import DiscountImage from '../../assets/images/3d-icons/discount.png';
import PackageImage from '../../assets/images/3d-icons/package.png';
import ContractsUtils from '../../common/utils/contracts.utils';
import ConfirmDialog from '../../common/components/ConfirmDialog';
import { currentContextAtom } from '../../state';
import { ContractConditions } from '../state';
import CommissionInfo from '../../common/components/CommissionInfo';
import { formatMinimumOrderValue } from '../../common/utils/common.utils';
import OpenPackagingPricesButton from './OpenPackagingPricesButton';

export interface AcceptOfferDialogProps {
  open: boolean;
  onClose: (confirm: boolean) => void;
  offerInfo: ContractConditions;
}

export default function AcceptOfferDialog({ open, onClose, offerInfo }: AcceptOfferDialogProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'contracts', 'packaging']);
  const { realm, commissionRate } = useAtomValue(currentContextAtom);

  const priceModeLabel = useMemo((): string => {
    return ContractsUtils.getPriceModeLabel(t, realm, offerInfo.discount, offerInfo.customPriceListName);
  }, [offerInfo]);

  const shouldDisplayPricesButton = realm === Business_Profile_Enum.Buyer && (!!offerInfo.custom_price_list_id || !!offerInfo.discount);

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={t('contracts:accept_commercial_offer')}
      subTitle={realm === Business_Profile_Enum.Buyer ? t('contracts:make_first_order_as_buyer') : t('contracts:make_first_order_as_supplier')}
      confirmLabel={t('contracts:accept')}
      cancelLabel={t('common:cancel')}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 4,
            width: 268,
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            py: 3,
            px: 2,
            mt: 3,
          }}
        >
          <Box>
            <Typography variant="titleLarge" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src={PackageImage} height={24} width={24} /> {t('common:minimum_order_value')}
            </Typography>
            <Box sx={{ display: 'flex', height: 40, alignItems: 'center', pl:1 }}>
              <Typography variant="bodyMedium">
                {formatMinimumOrderValue(t, offerInfo.minimum_order_value!, offerInfo.minimum_order_value_type)}
              </Typography>
            </Box>
          </Box>
          {priceModeLabel && (
            <Box>
              <Typography variant="titleLarge" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img src={DiscountImage} height={24} width={24} /> {t('contracts:discount')}
              </Typography>
              <Box sx={{ display: 'flex', height: 40, alignItems: 'center', pl:1 }}>
                <Typography variant="bodyMedium">
                  {priceModeLabel}
                </Typography>
                {shouldDisplayPricesButton && (
                  <OpenPackagingPricesButton offerInfo={offerInfo} />
                )}
              </Box>
            </Box>
          )}
        </Box>
        {realm === Business_Profile_Enum.Supplier && <CommissionInfo commission={commissionRate!} />}
      </Box>
    </ConfirmDialog>
  );
}
